import { Box, CircularProgress, Typography } from '@mui/material';
import { Ban, Check, ChevronLeft, CircleAlert, Download } from 'lucide-react';
import { useIntl } from 'react-intl';
import ActivePatientRecordIcon from '~/assets/active-patient-records.svg';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Separated } from '~/components/Separated';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import {
    ageInYears,
    formatDayText,
    formatPatientName,
    sexAgeText,
} from '~/containers/patient-list/model';
import {
    PatientIdText,
    PrimaryButton,
    QuietButton,
} from '~/containers/patient-list/shared';
import ModalityChip from '~/components/Chip/ModalityChip';
import { dicomDateToIso } from '~/domain/dicom/importScan';
import type { UsePacsImport } from '../hooks';
import {
    arrivedWhole,
    conflictingPatientIds,
    formatImportMessage,
    remoteModalityCounts,
    remoteSelectionSummary,
    resultsBySource,
    summariseArrivals,
    type PacsImportState,
    type PacsLookup,
    type PacsTarget,
    type RemotePatientIdentity,
    type RemoteSeriesRow,
    type SeriesArrival,
} from '../model';
import ImportEmptyState from './ImportEmptyState';
import PacsLookupForm from './PacsLookupForm';
import PacsPatientTable, { type SourceResults } from './PacsPatientTable';
import PacsSeriesTable from './PacsSeriesTable';

/**
 * The dialog's DICOM-source pane: choose the sources, look a patient up across all of them, choose
 * which series to fetch and where each is fetched from.
 *
 * It ends where the disk path does not. Retrieved instances are pushed to the connector's own
 * listener over a separate association and appear in the patient list behind this dialog, so the
 * last thing this pane can honestly report is what it asked for, never how far along the transfer
 * is. There is deliberately no progress bar and no cancel: the connector does not expose a
 * cancellable transfer, and drawing either would promise something that cannot be delivered.
 */

const capSx = {
    fontSize: 10,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: 'text.secondary',
};

/**
 * Name, id and demographics as the remote reported them. Masked, and never raw DICOM PN.
 *
 * Laid out as the worklist's own patient headers are: the name, then sex and age with the birth
 * date in parentheses, then the id last behind a rule. A reader checking they are about to retrieve
 * the right person should not have to re-learn where each part sits between one panel and the next.
 * Only the date itself is blurred; the "Male 86y (" around it is chrome, and masking it would cost
 * legibility without hiding anything.
 */
function RemoteIdentityLine({ patient }: { patient: RemotePatientIdentity }) {
    const intl = useIntl();
    const name = formatPatientName(patient.dicomPatientName);
    const birthDateIso = dicomDateToIso(patient.patientBirthDate);
    const sexAge = sexAgeText(
        intl,
        patient.patientSex,
        ageInYears(birthDateIso, new Date())
    );
    const birthDay = formatDayText(intl, birthDateIso);
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                overflow: 'hidden',
            }}
        >
            <Typography
                component="span"
                noWrap
                sx={{ fontSize: 15, fontWeight: 500, flexShrink: 0 }}
            >
                {name ? (
                    <SensitiveInfoTypography>{name}</SensitiveInfoTypography>
                ) : (
                    intl.formatMessage({
                        id: 'patientList.import.pacs.unnamedPatient',
                    })
                )}
            </Typography>
            <Separated>
                {sexAge || birthDay ? (
                    <Box
                        component="span"
                        sx={{
                            fontFamily: 'fontFamilyMonospace',
                            fontSize: 12,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {sexAge}
                        {birthDay && (
                            <>
                                {sexAge ? ' ' : ''}(
                                <SensitiveInfoTypography>
                                    {birthDay}
                                </SensitiveInfoTypography>
                                )
                            </>
                        )}
                    </Box>
                ) : null}
                <PatientIdText id={patient.dicomPatientId} />
            </Separated>
        </Box>
    );
}

/**
 * A line under the band stating what the list below does not show: a capped answer, or a source that
 * could not answer. A partial list is never presented as the whole of what the sources hold.
 */
function ListNote({ text }: { text: string }) {
    return (
        <Typography
            sx={{
                fontSize: 12,
                color: 'text.secondary',
                padding: '8px 16px',
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            {text}
        </Typography>
    );
}

// Through the shared frame like the other bodiless stages, so the line does not jump when the
// question resolves into its answer.
function Spinner({ labelId }: { labelId: string }) {
    const intl = useIntl();
    return (
        <ImportEmptyState
            graphic={<CircularProgress size={18} />}
            title={intl.formatMessage({ id: labelId })}
        />
    );
}

/**
 * Who the series below belong to, and the way back to the list they were picked from.
 *
 * Pinned above the scroll rather than sitting in it, matching the disk path's identity card. The
 * retrieval is issued for this patient and lands under their record, so a list long enough to carry
 * the subject off-screen while the commit button stays nailed to the bar is the exact hazard this
 * dialog exists to prevent.
 *
 * Back returns to the results already in hand: a mis-click on a name that matched several people
 * must not cost another query against the archive, because a user who has to pay for a correction
 * is a user who starts guessing instead.
 */
function SubjectBar({
    patient,
    onBack,
}: {
    patient: RemotePatientIdentity;
    onBack?: () => void;
}) {
    const intl = useIntl();
    return (
        <Box
            sx={{
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 16px',
                bgcolor: 'background.sunken',
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            {onBack && (
                <QuietButton onClick={onBack}>
                    <ChevronLeft size={13} />
                    {intl.formatMessage({
                        id: 'patientList.import.pacs.backToResults',
                    })}
                </QuietButton>
            )}
            <RemoteIdentityLine patient={patient} />
        </Box>
    );
}

/**
 * What the selection amounts to, and the one press that acts on it.
 *
 * There is deliberately no select-every-study control. The listing arrives fully selected, so such
 * a control would only ever clear in one press what the study bands are there to have the user
 * weigh one timepoint at a time.
 */
function CompositionBar({
    rows,
    selected,
    progress,
    canRequest,
    isRequesting,
    hint,
    onRequest,
}: {
    rows: readonly RemoteSeriesRow[];
    selected: ReadonlySet<string>;
    /**
     * How many of the selected series have been asked for so far, whatever the request came back
     * as: accepted, refused, or unanswered.
     */
    progress?: { asked: number; total: number };
    canRequest: boolean;
    isRequesting: boolean;
    hint: string;
    onRequest: () => void;
}) {
    const intl = useIntl();
    const summary = remoteSelectionSummary(rows, selected);
    const modalities = remoteModalityCounts(rows, selected);
    return (
        <Box
            sx={{
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 16px',
                borderTop: 1,
                borderColor: 'divider',
                // The row carries a caption, a chip per modality, a composition line and the
                // action. A flex child refuses to shrink below its content by default, so without
                // this the row grows past the dialog and takes the action off screen.
                minWidth: 0,
            }}
        >
            <Typography sx={{ ...capSx, flex: 'none' }}>
                {intl.formatMessage({
                    id: 'patientList.import.pacs.willRetrieve',
                })}
            </Typography>
            {modalities.map(({ modality, count }) => (
                <ModalityChip
                    key={modality}
                    modality={modality}
                    count={count}
                />
            ))}
            <Typography
                variant="monoMeta"
                sx={{
                    ml: 'auto',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {intl.formatMessage(
                    {
                        id:
                            summary.instanceCount === undefined
                                ? 'patientList.import.pacs.composition'
                                : 'patientList.import.pacs.compositionWithInstances',
                    },
                    {
                        studies: summary.studyCount,
                        series: summary.seriesCount,
                        instances: summary.instanceCount ?? 0,
                    }
                )}
            </Typography>
            {progress && (
                <Typography
                    role="status"
                    variant="monoMeta"
                    sx={{ flex: 'none', whiteSpace: 'nowrap' }}
                >
                    {intl.formatMessage(
                        { id: 'patientList.import.pacs.requestingProgress' },
                        progress
                    )}
                </Typography>
            )}
            <Box sx={{ flex: 'none' }}>
                <DisabledHint title={hint} placement="bottom-end">
                    <PrimaryButton
                        onClick={onRequest}
                        disabled={!canRequest || isRequesting}
                    >
                        {isRequesting ? (
                            <CircularProgress
                                size={12}
                                sx={{ color: '#fff' }}
                            />
                        ) : (
                            <Download size={12} />
                        )}
                        {intl.formatMessage({
                            id: 'patientList.import.pacs.importSelected',
                        })}
                    </PrimaryButton>
                </DisabledHint>
            </Box>
        </Box>
    );
}

/**
 * Terminal report for a retrieval nothing was accepted for.
 *
 * A refusal, an unanswered request and a series the run never asked for are reported apart, and the
 * heading takes the weaker of the claims: only a source that answered every request with a refusal
 * licenses saying that nothing was retrieved and nothing is coming. One request the connector never
 * answered may have started a transfer, and this dialog cannot tell.
 */
function NothingAcceptedReport({
    stage,
    onAnother,
}: {
    stage: Extract<PacsImportState['stage'], { kind: 'refused' }>;
    onAnother: () => void;
}) {
    const intl = useIntl();
    const hasUnanswered = stage.unanswered > 0;
    return (
        <Box
            sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1.5,
            }}
        >
            <Typography
                role="status"
                sx={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: hasUnanswered ? 'warning.main' : 'error.main',
                }}
            >
                {intl.formatMessage({
                    id: hasUnanswered
                        ? 'patientList.import.pacs.unanswered.title'
                        : 'patientList.import.pacs.refused.title',
                })}
            </Typography>
            {stage.refused > 0 && (
                <Typography
                    sx={{
                        fontSize: 12,
                        color: 'text.secondary',
                        maxWidth: 480,
                    }}
                >
                    {intl.formatMessage(
                        { id: 'patientList.import.pacs.refused.note' },
                        { count: stage.refused }
                    )}
                    {!hasUnanswered &&
                        ` ${intl.formatMessage({
                            id: 'patientList.import.pacs.refused.nothingSent',
                        })}`}
                </Typography>
            )}
            {hasUnanswered && (
                <Typography
                    sx={{
                        fontSize: 12,
                        color: 'text.secondary',
                        maxWidth: 480,
                    }}
                >
                    {intl.formatMessage(
                        { id: 'patientList.import.pacs.unanswered.note' },
                        { count: stage.unanswered }
                    )}
                </Typography>
            )}
            {stage.notRequested > 0 && (
                <Typography
                    sx={{
                        fontSize: 12,
                        color: 'text.secondary',
                        maxWidth: 480,
                    }}
                >
                    {intl.formatMessage(
                        { id: 'patientList.import.pacs.notRequested.note' },
                        { count: stage.notRequested }
                    )}
                </Typography>
            )}
            <QuietButton onClick={onAnother}>
                {intl.formatMessage({
                    id: 'patientList.import.pacs.lookUpAnother',
                })}
            </QuietButton>
        </Box>
    );
}

/**
 * One requested series and how much of it the connector holds.
 *
 * The figure is a count, never a bar or a percentage. The instances are pushed to the connector
 * over an association this dialog cannot observe, so a proportion drawn here would not be measured
 * from the transfer. The count is the connector's own tally of what it has stored against what it
 * recorded as expected before the transfer began, and it stays honest by being exact.
 */
function ArrivalRow({ arrival }: { arrival: SeriesArrival }) {
    const intl = useIntl();
    const label =
        arrival.seriesDescription ||
        intl.formatMessage({ id: 'patientList.import.pacs.untitledSeries' });
    const failed = arrival.state === 'failed';
    const arrived = arrival.state === 'arrived';
    const stalled = arrival.state === 'stalled';
    // The source cannot produce this object as DICOM, so nothing went wrong here and nothing can be
    // retried. Drawn plainly rather than as a fault: an error against a plan the planning system
    // itself will never export sends the reader looking for a problem on this side.
    const unavailable = arrival.state === 'unavailable';
    // Withdrawn before the source was asked: neither a fault nor a fact about the source.
    const cancelled = arrival.state === 'cancelled';
    // The connector's own reason token when it recorded one. It is what tells one failure from
    // another on a row with no room for a sentence, and it names no patient and no study.
    const failedText = intl.formatMessage(
        {
            id:
                arrival.failureReason === undefined
                    ? 'patientList.import.pacs.arrival.failed'
                    : 'patientList.import.pacs.arrival.failedWithReason',
        },
        { reason: arrival.failureReason }
    );
    // A series that stopped part way keeps its figure: the icon and the colour already say it
    // stopped, and only the count says how far it got. "Nothing received" is reserved for a series
    // the connector holds nothing of, which is a fact about its record rather than about the
    // instance tally it keeps. No figure at all for a dataset it keeps no tally for: any count
    // here would be one this pane invented.
    const countText = unavailable
        ? intl.formatMessage({
              id:
                  arrival.retrievalState === 'emptyAtSource'
                      ? 'patientList.import.pacs.arrival.emptyAtSource'
                      : 'patientList.import.pacs.arrival.notExportable',
          })
        : cancelled
          ? intl.formatMessage({
                id: 'patientList.import.pacs.arrival.cancelled',
            })
          : failed
            ? failedText
            : stalled && !arrival.anyReceived
              ? intl.formatMessage({
                    id: 'patientList.import.pacs.arrival.stalled',
                })
              : arrival.received === undefined
                ? undefined
                : arrival.expected === undefined
                  ? intl.formatMessage(
                        { id: 'patientList.import.pacs.arrival.received' },
                        { received: arrival.received }
                    )
                  : intl.formatMessage(
                        { id: 'patientList.import.pacs.arrival.ofExpected' },
                        {
                            received: arrival.received,
                            expected: arrival.expected,
                        }
                    );

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.75,
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    width: 16,
                    display: 'flex',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: arrived
                        ? 'success.main'
                        : failed
                          ? 'error.main'
                          : stalled
                            ? 'warning.main'
                            : 'text.secondary',
                }}
            >
                {arrived ? (
                    <Check size={13} />
                ) : unavailable || cancelled ? (
                    <Ban size={13} />
                ) : failed || stalled ? (
                    <CircleAlert size={13} />
                ) : (
                    <CircularProgress size={12} color="inherit" />
                )}
            </Box>
            {arrival.modality && <ModalityChip modality={arrival.modality} />}
            <Typography
                sx={{
                    fontSize: 12,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography
                sx={{
                    fontSize: 11,
                    fontFamily: 'fontFamilyMonospace',
                    color: failed
                        ? 'error.main'
                        : stalled
                          ? 'warning.main'
                          : 'text.secondary',
                    flexShrink: 0,
                }}
            >
                {countText}
            </Typography>
        </Box>
    );
}

/**
 * Why a series is stalled. Each cause names a different thing to check, and the sentence for one
 * is false for the others: the connector concluded that it delivered and nothing of the series is
 * under this patient; no verdict reached this dialog and the series stopped part way through; no
 * verdict reached this dialog and the connector holds nothing of the series at all.
 */
type StalledCause =
    | 'deliveredElsewhere'
    | 'stoppedArriving'
    | 'nothingReceived';

function stalledCause(arrival: SeriesArrival): StalledCause {
    return arrival.outcome === 'Delivered'
        ? 'deliveredElsewhere'
        : arrival.anyReceived
          ? 'stoppedArriving'
          : 'nothingReceived';
}

/**
 * The arrival of everything one retrieval asked for, while it happens and once it has settled.
 *
 * No control here may imply the transfer can be stopped: it is not cancellable, and the dialog is
 * dismissible precisely because closing it changes nothing about what is already on its way.
 */
function ArrivalReport({
    stage,
}: {
    stage: Extract<PacsImportState['stage'], { kind: 'arriving' | 'arrived' }>;
}) {
    const intl = useIntl();
    const summary = summariseArrivals(stage.arrivals);
    const settled = stage.kind === 'arrived';
    const stalledBy = (cause: StalledCause): number =>
        stage.arrivals.filter(
            (arrival) =>
                arrival.state === 'stalled' && stalledCause(arrival) === cause
        ).length;
    const deliveredElsewhere = stalledBy('deliveredElsewhere');
    const stoppedArriving = stalledBy('stoppedArriving');
    const nothingReceived = stalledBy('nothingReceived');
    // Only when the connector delivered every series and nothing was refused: a report that
    // settled short of that may not be presented as a finished import.
    const allArrived = arrivedWhole(stage);

    // Instances are the unit while every series keeps a tally; a selection carrying a dataset the
    // connector counts no instances for falls back to the series, which are known exactly.
    const headline = settled
        ? allArrived
            ? intl.formatMessage(
                  { id: 'patientList.import.pacs.arrival.allArrived' },
                  { count: summary.arrived }
              )
            : summary.failed > 0
              ? intl.formatMessage(
                    { id: 'patientList.import.pacs.arrival.someFailed' },
                    { arrived: summary.arrived, failed: summary.failed }
                )
              : summary.unavailable > 0
                ? intl.formatMessage(
                      {
                          id: 'patientList.import.pacs.arrival.someUnavailable',
                      },
                      {
                          arrived: summary.arrived,
                          unavailable: summary.unavailable,
                      }
                  )
                : intl.formatMessage(
                      { id: 'patientList.import.pacs.arrival.incomplete' },
                      {
                          arrived: summary.arrived,
                          total: stage.arrivals.length,
                      }
                  )
        : summary.received === undefined
          ? intl.formatMessage(
                { id: 'patientList.import.pacs.arrival.receivingSeries' },
                { arrived: summary.arrived, total: stage.arrivals.length }
            )
          : summary.expected === undefined
            ? intl.formatMessage(
                  { id: 'patientList.import.pacs.arrival.receivingUnknown' },
                  { received: summary.received }
              )
            : intl.formatMessage(
                  { id: 'patientList.import.pacs.arrival.receiving' },
                  { received: summary.received, expected: summary.expected }
              );

    return (
        <Box
            sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 1.5,
                minWidth: 0,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {allArrived && (
                    <Box
                        sx={{
                            color: 'success.main',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Check size={18} />
                    </Box>
                )}
                <Typography
                    role="status"
                    sx={{
                        fontSize: 15,
                        fontWeight: 500,
                        color: allArrived ? 'success.main' : 'text.primary',
                    }}
                >
                    {headline}
                </Typography>
            </Box>

            {stage.refused > 0 && (
                <Typography sx={{ fontSize: 12, color: 'error.main' }}>
                    {intl.formatMessage(
                        { id: 'patientList.import.pacs.requested.refused' },
                        { count: stage.refused }
                    )}
                </Typography>
            )}

            {/* Not folded into the refused line: a series whose request went unanswered may be
                arriving over an association this dialog cannot see. */}
            {stage.unanswered > 0 && (
                <Typography sx={{ fontSize: 12, color: 'warning.main' }}>
                    {intl.formatMessage(
                        { id: 'patientList.import.pacs.unanswered.note' },
                        { count: stage.unanswered }
                    )}
                </Typography>
            )}

            {/* Reported apart from both: no request was issued for these series, so nothing is
                arriving for them and nothing is duplicated by asking again. */}
            {stage.notRequested > 0 && (
                <Typography sx={{ fontSize: 12, color: 'warning.main' }}>
                    {intl.formatMessage(
                        { id: 'patientList.import.pacs.notRequested.note' },
                        { count: stage.notRequested }
                    )}
                </Typography>
            )}

            {nothingReceived > 0 && (
                <Typography sx={{ fontSize: 12, color: 'warning.main' }}>
                    {intl.formatMessage(
                        { id: 'patientList.import.pacs.arrival.stalledNote' },
                        { count: nothingReceived }
                    )}
                </Typography>
            )}

            {stoppedArriving > 0 && (
                <Typography sx={{ fontSize: 12, color: 'warning.main' }}>
                    {intl.formatMessage(
                        {
                            id: 'patientList.import.pacs.arrival.stoppedArrivingNote',
                        },
                        { count: stoppedArriving }
                    )}
                </Typography>
            )}

            {deliveredElsewhere > 0 && (
                <Typography sx={{ fontSize: 12, color: 'warning.main' }}>
                    {intl.formatMessage(
                        {
                            id: 'patientList.import.pacs.arrival.deliveredElsewhereNote',
                        },
                        { count: deliveredElsewhere }
                    )}
                </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {stage.arrivals.map((arrival) => (
                    <ArrivalRow key={arrival.key} arrival={arrival} />
                ))}
            </Box>

            {/* Nothing landed, so there is nothing behind this dialog to point the reader at. */}
            {(!settled || summary.arrived > 0) && (
                <Typography
                    sx={{
                        fontSize: 12,
                        color: 'text.secondary',
                        maxWidth: 480,
                    }}
                >
                    {intl.formatMessage({
                        id: settled
                            ? 'patientList.import.pacs.arrival.settledNote'
                            : 'patientList.import.pacs.arrival.keepsArriving',
                    })}
                </Typography>
            )}
        </Box>
    );
}

/**
 * What the current lookup has produced so far, source by source.
 *
 * Every included source is listed from the moment it is asked, so a slow or unreachable source
 * shows as such instead of silently contributing nothing. With one source there is nothing to tell
 * apart, and its status is carried by the list itself or by the empty state that replaces it.
 */
function LookupResults({
    lookup,
    sources,
    targets,
    onSelect,
}: {
    lookup: PacsLookup;
    sources: readonly string[];
    targets: readonly PacsTarget[];
    onSelect: UsePacsImport['actions']['selectPatient'];
}) {
    const intl = useIntl();
    const groups: SourceResults[] = resultsBySource(
        lookup.bySource,
        sources
    ).map(({ source, search }) => ({
        source,
        search,
        unreachable:
            targets.find((target) => target.name === source)?.isReachable ===
            false,
    }));
    const multiSource = groups.length > 1;
    const total = groups.reduce(
        (sum, group) => sum + group.search.patients.length,
        0
    );
    const settled = groups.every(
        (group) => group.search.status !== 'searching'
    );
    const failed = groups.filter((group) => group.search.status === 'failed');

    if (total === 0) {
        if (!settled) {
            return <Spinner labelId="patientList.import.pacs.lookingUp" />;
        }
        if (failed.length === groups.length) {
            return (
                <ImportEmptyState
                    isAlert
                    title={intl.formatMessage({
                        id: 'patientList.import.pacs.error.lookupFailed',
                    })}
                    description={failed.map((group) => group.source).join(', ')}
                />
            );
        }
        return (
            <ImportEmptyState
                isAlert
                title={intl.formatMessage({
                    id: 'patientList.import.pacs.noMatch',
                })}
                // An empty answer is the moment the match rule is actionable: without it a
                // mistyped id and a source that does not hold the patient read alike. A source
                // that failed was not searched, and the answer must not read as if it had been.
                description={[
                    intl.formatMessage({
                        id: 'patientList.import.pacs.matchingRules',
                    }),
                    failed.length > 0
                        ? intl.formatMessage(
                              {
                                  id: 'patientList.import.pacs.sourcesNotSearched',
                              },
                              {
                                  sources: failed
                                      .map((group) => group.source)
                                      .join(', '),
                              }
                          )
                        : undefined,
                ]
                    .filter(Boolean)
                    .join(' ')}
            />
        );
    }

    return (
        <>
            {!multiSource && groups[0]?.search.truncated && (
                <ListNote
                    text={intl.formatMessage({
                        id: 'patientList.import.pacs.morePatients',
                    })}
                />
            )}
            <PacsPatientTable
                groups={groups}
                showSourceHeadings={multiSource}
                conflictingIds={conflictingPatientIds(lookup.bySource)}
                onSelect={onSelect}
            />
        </>
    );
}

export default function PacsSourcePanel({ pacs }: { pacs: UsePacsImport }) {
    const intl = useIntl();
    const { state, actions } = pacs;
    const stage = state.stage;
    const busyHint = pacs.isRequesting
        ? intl.formatMessage({ id: 'patientList.import.pacs.hint.requesting' })
        : undefined;
    const onBack = pacs.canGoBackToResults ? actions.backToResults : undefined;
    const subject = 'patient' in stage ? stage.patient : undefined;
    // Rows name their source whenever more than one was searched, including when only one of them
    // holds the patient: that is itself where the series are.
    const showSources = state.sources.length > 1;

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <PacsLookupForm pacs={pacs} />

            {state.error !== undefined && (
                <Typography
                    role="alert"
                    sx={{
                        flex: 'none',
                        py: 1,
                        px: 2,
                        fontSize: 12,
                        color: 'error.main',
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    {formatImportMessage(intl, state.error)}
                </Typography>
            )}

            {/* Outside the scroller on every stage that has a subject, so no list can carry the
                identity of the patient being retrieved off the screen. */}
            {subject && <SubjectBar patient={subject} onBack={onBack} />}

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {stage.kind === 'search' && state.lookup === undefined && (
                    <ImportEmptyState
                        graphic={
                            // Decorative, so it stays out of the accessibility tree: the prompt
                            // below already carries the whole of what this screen says.
                            <Box
                                component="img"
                                src={ActivePatientRecordIcon}
                                alt=""
                                sx={{ width: '100%', height: '100%' }}
                            />
                        }
                        title={intl.formatMessage({
                            id: 'patientList.import.pacs.lookUpPrompt',
                        })}
                        description={intl.formatMessage({
                            id: 'patientList.import.pacs.lookUpHint',
                        })}
                    />
                )}

                {stage.kind === 'search' && state.lookup !== undefined && (
                    <LookupResults
                        lookup={state.lookup}
                        sources={state.sources}
                        targets={pacs.targets}
                        onSelect={actions.selectPatient}
                    />
                )}

                {stage.kind === 'loading-studies' && (
                    <Spinner labelId="patientList.import.pacs.readingSeries" />
                )}

                {(stage.kind === 'browsing' || stage.kind === 'requesting') && (
                    <>
                        {stage.kind === 'browsing' && stage.truncated && (
                            <ListNote
                                text={intl.formatMessage({
                                    id: 'patientList.import.pacs.moreStudies',
                                })}
                            />
                        )}
                        {stage.kind === 'browsing' &&
                            stage.unlisted.length > 0 && (
                                <ListNote
                                    text={intl.formatMessage(
                                        {
                                            id: 'patientList.import.pacs.seriesNotListed',
                                        },
                                        { sources: stage.unlisted.join(', ') }
                                    )}
                                />
                            )}
                        <PacsSeriesTable
                            rows={stage.rows}
                            selected={stage.selected}
                            retrieveFrom={stage.retrieveFrom}
                            showSources={showSources}
                            // The reducer ignores an edit once the retrievals are being issued, so
                            // the controls must not take a press they will drop.
                            disabled={stage.kind === 'requesting'}
                            onToggle={actions.toggleSeries}
                            onToggleGroup={actions.toggleSeriesGroup}
                            onChooseSource={actions.chooseSeriesSource}
                        />
                    </>
                )}

                {(stage.kind === 'arriving' || stage.kind === 'arrived') && (
                    <ArrivalReport stage={stage} />
                )}
                {stage.kind === 'refused' && (
                    <NothingAcceptedReport
                        stage={stage}
                        onAnother={actions.reset}
                    />
                )}
            </Box>

            {(stage.kind === 'browsing' || stage.kind === 'requesting') && (
                <CompositionBar
                    rows={stage.rows}
                    selected={stage.selected}
                    progress={
                        stage.kind === 'requesting'
                            ? {
                                  asked:
                                      stage.accepted +
                                      stage.refused +
                                      stage.unanswered,
                                  total: stage.total,
                              }
                            : undefined
                    }
                    canRequest={pacs.canRequest}
                    isRequesting={pacs.isRequesting}
                    hint={
                        busyHint ??
                        (pacs.canRequest
                            ? ''
                            : intl.formatMessage({
                                  id: 'patientList.import.pacs.hint.needsSeries',
                              }))
                    }
                    onRequest={actions.request}
                />
            )}
        </Box>
    );
}
